import React, { useState } from 'react';

import { useClaim } from '~/components/ClaimContainer';
import type { ConductorStatusObservation } from '~/components/ClaimPage/CliveApp/conductorStatusObservation';
import { useConductorHandling } from '~/components/ClaimPage/CliveApp/hooks/useConductorHandling';
import { useConductorStatusObservation } from '~/components/ClaimPage/CliveApp/hooks/useConductorStatusObservation';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Text from '~/components/core/TextComponents/Text';
import { ClaimConductorHandlingResponseHandlingMode } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import GenericClaimBarField from '../GenericClaimBarField';

import { getStatusAppearance, TONE_DOT_CLASS } from './conductorStatus';
import TakeOverDialog from './TakeOverDialog';
import { TEXTS } from './texts';
import { useSwitchHandling } from './useSwitchHandling';

interface StatusSegment {
  dotClassName: string;
  text: string;
  tooltip: string | null;
}

/** Clive's status while Clive has the claim and it is known; otherwise only who has the claim. */
const getStatusSegment = (isClives: boolean, conductorStatus: ConductorStatusObservation | null): StatusSegment => {
  if (!isClives) return { dotClassName: 'bg-slate-700', text: TEXTS.HANDLER_STATUS, tooltip: null };
  // Idle's grey, not a status colour: the status is still loading, or could not be read.
  if (!conductorStatus) return { dotClassName: TONE_DOT_CLASS.idle, text: TEXTS.CONDUCTOR_STATUS, tooltip: null };

  const { tone, text, tooltip } = getStatusAppearance(conductorStatus);
  return { dotClassName: TONE_DOT_CLASS[tone], text, tooltip };
};

/**
 * Who drives the claim — Clive, or a handler who took it over — as a pill that also switches it.
 * While Clive drives it, the pill says what Clive is doing.
 *
 * Taking over asks first, with an optional reason, because it interrupts Clive; handing back does
 * not, so it is immediate. Nothing is drawn until the handling is read, nor where the read fails —
 * a claim on which Clive chat is unavailable, say — so the bar is never interrupted by an error.
 */
const ConductorHandling = ({ displayName }: { displayName: string }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { handling, reloadHandling } = useConductorHandling(claim.id);
  const switchHandling = useSwitchHandling(reloadHandling);
  const [isTakeOverOpen, setIsTakeOverOpen] = useState(false);
  const [isHandingBack, setIsHandingBack] = useState(false);
  const isClives = handling?.mode === ClaimConductorHandlingResponseHandlingMode.Conductor;
  const conductorStatus = useConductorStatusObservation({ enabled: isClives });

  if (!handling) return null;

  const statusSegment = getStatusSegment(isClives, conductorStatus);
  const status = (
    <span className="flex items-center gap-4">
      <span aria-hidden className={cn('inline-block h-8 w-8 shrink-0 rounded-full', statusSegment.dotClassName)} />
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} className="text-current">
        {statusSegment.text}
      </Text>
    </span>
  );

  // A failure rethrows into the dialog, which stays open to retry.
  const handleTakeOver = async (reason: string | undefined): Promise<void> => {
    await switchHandling('take-over', reason);
    setIsTakeOverOpen(false);
  };

  const handleHandBack = async (): Promise<void> => {
    setIsHandingBack(true);
    try {
      await switchHandling('hand-back');
    } catch {
      // Already reported by `useSwitchHandling`; the button comes back to try again.
    } finally {
      setIsHandingBack(false);
    }
  };

  // One pill is both the status and the switch; it turns solid while a handler has the claim, so the
  // exceptional state is the loud one. Taking over does the same whatever Clive is doing, so it is
  // offered in every status. Drawn in the `content` slot, as Stage draws its chip, so the two line up.
  // `m-0` cancels the 5px margin the shared chip class puts on every side, which would push this chip
  // below Stage's.
  return (
    <GenericClaimBarField
      title={displayName}
      allowWrapping={false}
      content={
        <Chip
          size="small"
          variant={isClives ? 'outlined' : 'filled'}
          color="primary"
          className="m-0"
          clickable
          onClick={isClives ? () => setIsTakeOverOpen(true) : handleHandBack}
          disabled={isHandingBack}
          label={
            <span className="flex items-center gap-4">
              {statusSegment.tooltip ? (
                <Tooltip title={<>{statusSegment.tooltip}</>} describeChild>
                  {status}
                </Tooltip>
              ) : (
                status
              )}
              <span aria-hidden className="opacity-50">
                |
              </span>
              <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} className="text-current">
                {isClives ? TEXTS.TAKE_OVER : TEXTS.HAND_BACK}
              </Text>
            </span>
          }
        />
      }
    >
      {isTakeOverOpen ? <TakeOverDialog onConfirm={handleTakeOver} onClose={() => setIsTakeOverOpen(false)} /> : null}
    </GenericClaimBarField>
  );
};

export default ConductorHandling;
